import type { IdeaLabWalkForwardYearSummary } from '@einhaus/project314-types';
import MarketGroupComparison from './MarketGroupComparison';
import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Callout, DataTable, Disclosure, StatusBadge, Tooltip } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import IdeaLabEntryTimingProfile from './IdeaLabEntryTimingProfile';
import ReportViewControls from './ReportViewControls';
import RunScoreboard from './RunScoreboard';
import {
    buildOptionReasonRows,
    buildReportMatrix,
    cellEntryCount,
    formatDollars,
    formatHorizonLabel,
    formatPlainPercent,
    formatShare,
    formatSignedDollars,
    formatSignedPercent,
    LENS_LABELS,
    percentToneClass,
    RANDOM_EDGE_BASIS_LABELS,
    tradabilityRate,
    WINDOW_ROLE_LABELS,
    type IdeaLabRunCell,
    type IdeaLabRunDetail,
    type IdeaLabRunDiffCell,
    type IdeaLabRunEntityCell,
    type IdeaLabRunReport,
    type IdeaLabRunVariant
} from '../ideaLabUtils';
import { reportForMarket, type ReportView } from '../reportView';

interface RunReportMatrixProps {
    report: IdeaLabRunReport;
    /** The run's frozen universe, used to label entity cells with symbols. */
    entities: IdeaLabRunDetail['entities'];
    view: ReportView;
    onViewChange: (patch: Partial<ReportView>) => void;
    /** Actions for the selected variant, e.g. promotion. */
    renderVariantActions?: (variant: IdeaLabRunVariant) => ReactNode;
}

const SIGNED_CELL = (value: number | null) => <span className={percentToneClass(value)}>{formatSignedPercent(value)}</span>;
const DOLLAR_CELL = (value: number | null) => <span className={percentToneClass(value)}>{formatSignedDollars(value)}</span>;

const SectionHeading = ({ children }: { children: ReactNode }) => (
    <h5 className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">{children}</h5>
);

/** Visual Validator deep link for one variant, lens, and horizon of a run. */
const buildEntryReviewPath = (runId: number, variantId: number, view: ReportView): string => {
    const params = new URLSearchParams({ variantId: String(variantId), lens: view.lens });

    if (view.entityType !== 'All') params.set('entityType', view.entityType);
    if (view.horizonSeconds !== null) params.set('horizonSeconds', String(view.horizonSeconds));

    return `/user/ideaLab/run/${runId}/review?${params.toString()}`;
};

/**
 * A run's report, results first: a scoreboard of every variant's metrics side by side for one lens,
 * window, and horizon (or the variant-by-horizon matrix of one metric), then the selected variant's
 * detail. Every number is descriptive: standard errors and confidence limits are withheld in this
 * release, and each random edge states whether its contextual reference is covered. The view is
 * controlled, so the caller can keep it in the URL.
 */
const RunReportMatrix = ({ report: fullReport, entities, view, onViewChange, renderVariantActions }: RunReportMatrixProps) => {
    const report = useMemo(() => reportForMarket(fullReport, view.entityType), [fullReport, view.entityType]);
    const { lens, windowRole, variantId: selectedVariantId } = view;
    const symbolByEntity = useMemo(
        () => new Map(entities.map((entity) => [`${entity.entityType}:${entity.entityId}`, entity.symbol])),
        [entities]
    );
    const variantKeyById = useMemo(
        () => new Map(report.variants.map((variant) => [variant.variantId, variant.variantKey])),
        [report.variants]
    );
    const matrix = useMemo(() => buildReportMatrix(report, lens, windowRole, view.metric), [report, lens, windowRole, view.metric]);
    const selectedVariant = report.variants.find((variant) => variant.variantId === selectedVariantId) ?? null;
    const isOptionLens = lens === 'option';

    const entityCells = useMemo(
        () =>
            report.entityCells.filter(
                (cell) => cell.variantId === selectedVariantId && cell.windowRole === windowRole && cell.lens === lens
            ),
        [report.entityCells, selectedVariantId, windowRole, lens]
    );
    const diffCells = useMemo(
        () => report.diffs.filter((cell) => cell.variantId === selectedVariantId && cell.windowRole === windowRole && cell.lens === lens),
        [report.diffs, selectedVariantId, windowRole, lens]
    );
    const optionReasonRows = useMemo(
        () => (lens === 'option' ? buildOptionReasonRows(report, selectedVariantId) : []),
        [report, selectedVariantId, lens]
    );
    const optionLifecycles = useMemo(
        () =>
            report.optionLifecycles
                .filter((row) => row.variantId === selectedVariantId)
                .sort((left, right) => left.horizonSeconds - right.horizonSeconds || left.lifecycle.localeCompare(right.lifecycle)),
        [report.optionLifecycles, selectedVariantId]
    );
    const optionSelections = useMemo(
        () =>
            report.optionSelections
                .filter((row) => row.variantId === selectedVariantId)
                .sort((left, right) => left.horizonSeconds - right.horizonSeconds),
        [report.optionSelections, selectedVariantId]
    );
    const variantCells = useMemo(
        () =>
            report.cells
                .filter((cell) => cell.variantId === selectedVariantId && cell.windowRole === windowRole && cell.lens === lens)
                .sort((left, right) => left.horizonSeconds - right.horizonSeconds),
        [report.cells, selectedVariantId, windowRole, lens]
    );

    const yearlyRows = report.walkForwardYears.filter(
        (row) =>
            row.variantId === selectedVariantId &&
            row.lens === lens &&
            (view.horizonSeconds === null || row.horizonSeconds === view.horizonSeconds)
    );
    const yearlyColumns: ColumnDef<IdeaLabWalkForwardYearSummary>[] = [
        {
            accessorKey: 'yearNumber',
            header: 'Year',
            cell: ({ row }) => (
                <Tooltip content={`${row.original.startDateTime} → ${row.original.endDateTime} (exclusive), Eastern time`}>
                    <span>
                        {row.original.yearNumber}: {row.original.startDateTime.slice(0, 10)} → {row.original.endDateTime.slice(0, 10)}
                    </span>
                </Tooltip>
            )
        },
        { accessorKey: 'entityType', header: 'Market' },
        { accessorKey: 'horizonSeconds', header: 'Horizon', cell: ({ row }) => formatHorizonLabel(row.original.horizonSeconds) },
        { accessorKey: 'supportCount', header: 'Priced' },
        { accessorKey: 'missingCount', header: 'Missing' },
        { accessorKey: 'immatureCount', header: 'Immature' },
        { accessorKey: 'meanReturnPercent', header: 'Mean', cell: ({ row }) => SIGNED_CELL(row.original.meanReturnPercent) },
        { accessorKey: 'distinctEntityCount', header: 'Entities' },
        { accessorKey: 'distinctDateCount', header: 'Entry dates' }
    ];

    const selectVariant = (variantId: number) => onViewChange({ variantId });

    const randomEdgeColumn = <TCell extends IdeaLabRunCell | IdeaLabRunEntityCell>(): ColumnDef<TCell> => ({
        accessorKey: 'randomEdgeMeanPercent',
        header: 'Edge vs random',
        cell: ({ row }) => (
            <Tooltip
                content={`${row.original.randomEdgeCoveredCount} of ${row.original.supportCount} priced entries had a ${RANDOM_EDGE_BASIS_LABELS[row.original.randomEdgeBasis]}`}
            >
                <span>
                    {SIGNED_CELL(row.original.randomEdgeMeanPercent)}
                    <span className="ml-1 text-xs text-zinc-500">
                        {row.original.randomEdgeCoveredCount}/{row.original.supportCount}
                    </span>
                </span>
            </Tooltip>
        )
    });

    const optionColumns = <TCell extends IdeaLabRunCell | IdeaLabRunEntityCell>(): ColumnDef<TCell>[] =>
        isOptionLens
            ? [
                  {
                      id: 'tradability',
                      header: 'Tradable',
                      cell: ({ row }) => (
                          <Tooltip
                              content={`${row.original.option.noContractCount} entries had no matching contract; ${row.original.option.notEnteredCount} selected a contract that could not be entered or priced`}
                          >
                              <span>
                                  {formatPlainPercent(tradabilityRate(row.original), 0)}
                                  <span className="ml-1 text-xs text-zinc-500">of {cellEntryCount(row.original)}</span>
                              </span>
                          </Tooltip>
                      )
                  },
                  { id: 'netProfit', header: 'Net P&L', cell: ({ row }) => DOLLAR_CELL(row.original.option.meanNetProfit) },
                  { id: 'fees', header: 'Fees', cell: ({ row }) => formatDollars(row.original.option.meanFees) },
                  { id: 'premium', header: 'Premium', cell: ({ row }) => formatDollars(row.original.option.meanOpeningPremium) },
                  { id: 'riskCapital', header: 'Risk capital', cell: ({ row }) => formatDollars(row.original.option.meanRiskCapital) }
              ]
            : [];

    const detailColumns: ColumnDef<IdeaLabRunCell>[] = [
        { accessorKey: 'horizonSeconds', header: 'Horizon', cell: ({ row }) => formatHorizonLabel(row.original.horizonSeconds) },
        { accessorKey: 'supportCount', header: 'Priced' },
        { accessorKey: 'immatureCount', header: 'Immature' },
        { accessorKey: 'missingCount', header: 'Missing' },
        {
            id: 'winRate',
            header: 'Win rate',
            cell: ({ row }) =>
                row.original.supportCount === 0 ? '—' : `${((row.original.winCount / row.original.supportCount) * 100).toFixed(1)}%`
        },
        { accessorKey: 'meanReturnPercent', header: 'Mean', cell: ({ row }) => SIGNED_CELL(row.original.meanReturnPercent) },
        { accessorKey: 'medianReturnPercent', header: 'Median', cell: ({ row }) => SIGNED_CELL(row.original.medianReturnPercent) },
        {
            accessorKey: 'stdDevReturnPercent',
            header: 'Std dev',
            cell: ({ row }) => formatSignedPercent(row.original.stdDevReturnPercent).replace('+', '')
        },
        { accessorKey: 'meanMfePercent', header: 'Mean MFE', cell: ({ row }) => SIGNED_CELL(row.original.meanMfePercent) },
        { accessorKey: 'meanMaePercent', header: 'Mean MAE', cell: ({ row }) => SIGNED_CELL(row.original.meanMaePercent) },
        ...(isOptionLens
            ? []
            : [
                  {
                      accessorKey: 'benchmarkEdgeMeanPercent',
                      header: 'Edge vs SPY',
                      cell: ({ row }) => SIGNED_CELL(row.original.benchmarkEdgeMeanPercent)
                  } satisfies ColumnDef<IdeaLabRunCell>
              ]),
        randomEdgeColumn<IdeaLabRunCell>(),
        ...optionColumns<IdeaLabRunCell>(),
        { accessorKey: 'distinctEntityCount', header: 'Symbols' },
        { accessorKey: 'distinctDateCount', header: 'Dates' },
        { accessorKey: 'uncertaintyBlockCount', header: 'Time blocks' },
        { accessorKey: 'maxEntityShare', header: 'Top symbol share', cell: ({ row }) => formatShare(row.original.maxEntityShare) },
        { accessorKey: 'maxDateShare', header: 'Top date share', cell: ({ row }) => formatShare(row.original.maxDateShare) }
    ];

    const entityColumns: ColumnDef<IdeaLabRunEntityCell>[] = [
        {
            id: 'entity',
            header: 'Symbol',
            accessorFn: (cell) => symbolByEntity.get(`${cell.entityType}:${cell.entityId}`) ?? `${cell.entityType} #${cell.entityId}`,
            cell: ({ row }) => (
                <span>
                    <span className="text-zinc-100">
                        {symbolByEntity.get(`${row.original.entityType}:${row.original.entityId}`) ?? `#${row.original.entityId}`}
                    </span>
                    <span className="ml-1 text-xs text-zinc-500">{row.original.entityType}</span>
                </span>
            )
        },
        { accessorKey: 'horizonSeconds', header: 'Horizon', cell: ({ row }) => formatHorizonLabel(row.original.horizonSeconds) },
        { accessorKey: 'supportCount', header: 'Priced' },
        { accessorKey: 'immatureCount', header: 'Immature' },
        { accessorKey: 'missingCount', header: 'Missing' },
        { accessorKey: 'winCount', header: 'Wins' },
        { accessorKey: 'meanReturnPercent', header: 'Mean', cell: ({ row }) => SIGNED_CELL(row.original.meanReturnPercent) },
        { accessorKey: 'medianReturnPercent', header: 'Median', cell: ({ row }) => SIGNED_CELL(row.original.medianReturnPercent) },
        ...(isOptionLens
            ? []
            : [
                  {
                      accessorKey: 'benchmarkEdgeMeanPercent',
                      header: 'Edge vs SPY',
                      cell: ({ row }) => SIGNED_CELL(row.original.benchmarkEdgeMeanPercent)
                  } satisfies ColumnDef<IdeaLabRunEntityCell>
              ]),
        randomEdgeColumn<IdeaLabRunEntityCell>(),
        ...optionColumns<IdeaLabRunEntityCell>(),
        { accessorKey: 'distinctDateCount', header: 'Dates' }
    ];

    const diffColumns: ColumnDef<IdeaLabRunDiffCell>[] = [
        { accessorKey: 'horizonSeconds', header: 'Horizon', cell: ({ row }) => formatHorizonLabel(row.original.horizonSeconds) },
        {
            id: 'shared',
            header: 'Shared episodes',
            cell: ({ row }) => (
                <span>
                    {row.original.sharedCount} · {SIGNED_CELL(row.original.sharedMeanReturnPercent)}
                    <span className="ml-1 text-xs text-zinc-500">
                        rnd {formatSignedPercent(row.original.sharedRandomEdgeMeanPercent)} ({row.original.sharedRandomEdgeCoveredCount})
                    </span>
                </span>
            )
        },
        {
            id: 'added',
            header: 'Episodes added',
            cell: ({ row }) => (
                <span>
                    {row.original.addedCount} · {SIGNED_CELL(row.original.addedMeanReturnPercent)}
                    <span className="ml-1 text-xs text-zinc-500">
                        rnd {formatSignedPercent(row.original.addedRandomEdgeMeanPercent)} ({row.original.addedRandomEdgeCoveredCount})
                    </span>
                </span>
            )
        },
        {
            id: 'removed',
            header: 'Episodes removed',
            cell: ({ row }) => (
                <span>
                    {row.original.removedCount} · {SIGNED_CELL(row.original.removedMeanReturnPercent)}
                    <span className="ml-1 text-xs text-zinc-500">
                        rnd {formatSignedPercent(row.original.removedRandomEdgeMeanPercent)} ({row.original.removedRandomEdgeCoveredCount})
                    </span>
                </span>
            )
        },
        { accessorKey: 'randomEdgeBasis', header: 'Reference', cell: ({ row }) => RANDOM_EDGE_BASIS_LABELS[row.original.randomEdgeBasis] }
    ];

    const matrixTable = (
        <div className="overflow-x-auto rounded-md border border-zinc-800">
            <table className="w-full text-sm">
                <thead className="bg-[#131416]">
                    <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-400">
                        <th className="px-3 py-2 font-medium">Variant</th>
                        {matrix.horizons.map((horizon) => (
                            <th className="px-3 py-2 text-right font-medium" key={horizon}>
                                {formatHorizonLabel(horizon)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {matrix.rows.map((row) => {
                        const isSelected = row.variant.variantId === selectedVariantId;

                        return (
                            <tr
                                aria-selected={isSelected}
                                className={`cursor-pointer border-t border-zinc-800 ${isSelected ? 'bg-blue-500/10' : 'hover:bg-zinc-800/40'}`}
                                key={row.variant.variantId}
                                onClick={() => selectVariant(row.variant.variantId)}
                            >
                                <td className={`border-l-2 px-3 py-2 align-top ${isSelected ? 'border-blue-400' : 'border-transparent'}`}>
                                    <div className="font-medium text-zinc-100">{row.variant.label}</div>
                                    <div className="text-xs text-zinc-500">
                                        {row.variant.variantKey} · #{row.variant.variantId}
                                        {row.variant.role === 'control' ? ' · control' : ''}
                                        {row.variant.parentVariantId !== null
                                            ? ` · child of ${variantKeyById.get(row.variant.parentVariantId) ?? `#${row.variant.parentVariantId}`}`
                                            : ''}
                                    </div>
                                </td>
                                {matrix.horizons.map((horizon) => {
                                    const cell = row.cells.get(horizon);

                                    return (
                                        <td className="px-3 py-2 text-right align-top" key={horizon}>
                                            {cell ? (
                                                <Tooltip content={cell.caption}>
                                                    <span>
                                                        <span
                                                            className={
                                                                view.metric === 'supportCount' || view.metric === 'tradabilityRate'
                                                                    ? 'text-zinc-200'
                                                                    : percentToneClass(cell.value)
                                                            }
                                                        >
                                                            {cell.display}
                                                        </span>
                                                        <span className="block text-xs text-zinc-500">{cell.caption}</span>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <span className="text-zinc-600">—</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="space-y-4">
            {lens === 'futures' && (
                <Callout variant="info">
                    Each ETF signal is evaluated independently against its frozen native futures route. Price return is net cash divided by
                    absolute opening futures notional; return on reserve is reported separately. Excursions use possible modeled minute-open
                    exits after costs. Overlapping opportunities do not represent a funded account. No matched native random-entry reference
                    is available.
                </Callout>
            )}
            <MarketGroupComparison report={fullReport} view={view} />
            <ReportViewControls report={fullReport} showModeToggle view={view} onChange={onViewChange} />
            {report.cells.some(
                (cell) => cell.lens === lens && cell.windowRole === windowRole && (cell.missingCount > 0 || cell.immatureCount > 0)
            ) && (
                <Callout variant="warning">
                    Returns describe the priced subset. Valid entries with missing, defective, lifecycle or immature exit evidence remain in
                    the coverage counts. This comparison is conditional; it does not certify a winner for the complete cohort.
                </Callout>
            )}

            {report.outcomeCauses.some((row) => row.lens === lens && row.outcomeState === 'confirmedLifecycleUnavailable') && (
                <Callout variant="warning">
                    Recorded lifecycle events lack supported terminal values for{' '}
                    {report.outcomeCauses
                        .filter((row) => row.lens === lens && row.outcomeState === 'confirmedLifecycleUnavailable')
                        .reduce((sum, row) => sum + row.entryCount, 0)}{' '}
                    unique outcome coordinates across horizons. These entries remain unresolved rather than being removed as ordinary gaps.
                </Callout>
            )}

            {view.mode === 'scoreboard' ? (
                <RunScoreboard
                    report={report}
                    rowActionLabel="Show details"
                    selectedVariantId={selectedVariantId}
                    view={view}
                    onRowClick={selectVariant}
                />
            ) : (
                matrixTable
            )}

            <p className="text-xs leading-relaxed text-zinc-500">
                {view.mode === 'scoreboard'
                    ? 'Green outlines mark the best value in each column (returns and edges only when positive); click a column header to sort and a row to see its detail. '
                    : 'Click a row to see its detail. '}
                Every figure is descriptive: standard errors and confidence limits are withheld until an interval method is validated for
                overlapping fixed-horizon outcomes.
                {isOptionLens
                    ? ' Option returns are net dollars of one contract as a percentage of its opening premium, after platform commissions; risk capital is reported separately and is never the denominator.'
                    : ''}
            </p>

            {selectedVariant && (
                <section aria-label="Selected variant" className="space-y-5 rounded-lg border border-zinc-800 bg-black/20 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                                Selected variant · {LENS_LABELS[lens]} · {WINDOW_ROLE_LABELS[windowRole]}
                            </div>
                            <h4 className="mt-0.5 text-base font-semibold text-zinc-100">{selectedVariant.label}</h4>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                                <span>
                                    {selectedVariant.variantKey} · #{selectedVariant.variantId}
                                </span>
                                {selectedVariant.role === 'control' && (
                                    <StatusBadge status="control" tone="accent">
                                        Control
                                    </StatusBadge>
                                )}
                                <Link
                                    className="text-blue-300 hover:text-blue-200"
                                    to={`/user/ideaLab/variant/${selectedVariant.variantId}`}
                                >
                                    Variant page
                                </Link>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                size="sm"
                                to={buildEntryReviewPath(report.run.id, selectedVariant.variantId, view)}
                                variant="outline-dark"
                            >
                                Inspect entries on a chart
                            </Button>
                            {renderVariantActions?.(selectedVariant)}
                        </div>
                    </div>

                    <div>
                        <SectionHeading>All horizons</SectionHeading>
                        <DataTable columns={detailColumns} data={variantCells} search={false} shrinkToContent />
                    </div>

                    {windowRole === 'walkForward' && report.run.walkForwardStartDateTime !== null && (
                        <div>
                            <SectionHeading>Walk-forward by year</SectionHeading>
                            <p className="mb-2 text-xs text-zinc-400">
                                Entries grouped by execution year, using the same final outcome cutoff as the combined result. These are
                                descriptive slices of this exploratory run, not independent confirmation tests.
                            </p>
                            {yearlyRows.length > 0 ? (
                                <DataTable columns={yearlyColumns} data={yearlyRows} search={false} shrinkToContent />
                            ) : (
                                <p className="text-xs text-zinc-400">
                                    Annual detail is unavailable from retained entry facts for this selection.
                                </p>
                            )}
                        </div>
                    )}

                    {diffCells.length > 0 && (
                        <div>
                            <SectionHeading>
                                Versus parent {variantKeyById.get(diffCells[0]!.parentVariantId) ?? `#${diffCells[0]!.parentVariantId}`}, by
                                originating signal episode
                            </SectionHeading>
                            <DataTable columns={diffColumns} data={diffCells} search={false} shrinkToContent />
                        </div>
                    )}

                    {optionReasonRows.length > 0 && (
                        <div>
                            <SectionHeading>Entries without a priced option outcome (whole range)</SectionHeading>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-wide text-zinc-500">
                                            <th className="py-2 pr-4">Reason</th>
                                            {matrix.horizons.map((horizon) => (
                                                <th className="py-2 pr-4 text-right" key={horizon}>
                                                    {formatHorizonLabel(horizon)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {optionReasonRows.map((row) => (
                                            <tr className="border-b border-zinc-800/60" key={`${row.outcomeState}:${row.reasonCode}`}>
                                                <td className="py-1.5 pr-4">
                                                    <span className="text-zinc-200">{row.label}</span>
                                                    <span className="ml-2 text-xs text-zinc-500">
                                                        {row.outcomeState === 'immature' ? 'immature' : 'missing'} · {row.reasonCode}
                                                    </span>
                                                </td>
                                                {matrix.horizons.map((horizon) => (
                                                    <td className="py-1.5 pr-4 text-right text-zinc-300" key={horizon}>
                                                        {row.counts.get(horizon) ?? <span className="text-zinc-600">—</span>}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {isOptionLens && optionSelections.length > 0 && (
                        <div className="space-y-2">
                            <SectionHeading>Contract selection and expiration</SectionHeading>
                            {optionSelections.some((row) => row.expirationCrossedCount > 0) && (
                                <Callout variant="warning">
                                    One or more requested horizons crossed option expiration. Returns can include exercise or assignment and
                                    the later liquidation of the resulting shares, so a bought option can lose more than its opening
                                    premium.
                                </Callout>
                            )}
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-wide text-zinc-500">
                                            <th className="py-2 pr-4">Horizon</th>
                                            <th className="py-2 pr-4 text-right">Selected</th>
                                            <th className="py-2 pr-4 text-right">Crossed expiry</th>
                                            <th className="py-2 pr-4 text-right">Actual DTE</th>
                                            <th className="py-2 pr-4 text-right">Spread</th>
                                            <th className="py-2 pr-4 text-right">Strike range</th>
                                            <th className="py-2 pr-4">Lifecycle</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {optionSelections.map((selection) => {
                                            const lifecycleText = optionLifecycles
                                                .filter((row) => row.horizonSeconds === selection.horizonSeconds)
                                                .map((row) => `${row.lifecycle.replace(/([A-Z])/g, ' $1').toLowerCase()} ${row.entryCount}`)
                                                .join(' · ');

                                            return (
                                                <tr className="border-b border-zinc-800/60" key={selection.horizonSeconds}>
                                                    <td className="py-1.5 pr-4 text-zinc-200">
                                                        {formatHorizonLabel(selection.horizonSeconds)}
                                                    </td>
                                                    <td className="py-1.5 pr-4 text-right text-zinc-300">{selection.selectedCount}</td>
                                                    <td className="py-1.5 pr-4 text-right text-zinc-300">
                                                        {selection.expirationCrossedCount}
                                                    </td>
                                                    <td className="py-1.5 pr-4 text-right text-zinc-300">
                                                        {selection.meanEntryDteDays === null
                                                            ? '—'
                                                            : `${selection.meanEntryDteDays.toFixed(1)} avg (${selection.minimumEntryDteDays?.toFixed(1) ?? '—'}–${selection.maximumEntryDteDays?.toFixed(1) ?? '—'})`}
                                                    </td>
                                                    <td className="py-1.5 pr-4 text-right text-zinc-300">
                                                        {selection.meanSpreadPercent === null
                                                            ? '—'
                                                            : `${selection.meanSpreadPercent.toFixed(1)}% avg · p90 ${selection.p90SpreadPercent?.toFixed(1) ?? '—'}%`}
                                                    </td>
                                                    <td className="py-1.5 pr-4 text-right text-zinc-300">
                                                        {selection.minimumStrike === null
                                                            ? '—'
                                                            : `$${selection.minimumStrike.toFixed(2)}–$${selection.maximumStrike?.toFixed(2) ?? '—'}`}
                                                    </td>
                                                    <td className="py-1.5 pr-4 text-zinc-300">{lifecycleText || '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {variantCells.length > 0 && (
                        <Disclosure title="Entry timing by weekday and time of day" variant="card">
                            <IdeaLabEntryTimingProfile
                                entityType={view.entityType === 'All' ? undefined : view.entityType}
                                horizons={variantCells.map((cell) => cell.horizonSeconds)}
                                initialHorizonSeconds={view.horizonSeconds}
                                key={`${selectedVariant.variantId}:${lens}:${windowRole}`}
                                lens={lens}
                                runId={report.run.id}
                                variantId={selectedVariant.variantId}
                                variantLabel={selectedVariant.label}
                                windowRole={windowRole}
                            />
                        </Disclosure>
                    )}

                    <div>
                        <SectionHeading>Per symbol</SectionHeading>
                        <DataTable columns={entityColumns} data={entityCells} pagination={{ enabled: true, pageSize: 25 }} search sort />
                    </div>
                </section>
            )}
        </div>
    );
};

export default RunReportMatrix;
